import React, { useState, useRef } from 'react';
import { FaInstagram, FaFacebookF, FaLinkedinIn, FaGithub } from 'react-icons/fa';
import Spline from '@splinetool/react-spline';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
  });
  const [loaded, setLoaded] = useState(false);
  const [isAudioPlaying, setIsAudioPlaying] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [aiResponse, setAiResponse] = useState('');
  const debounceTimeoutRef = useRef(null);
  const recognitionRef = useRef(null);

  // Initialize SpeechRecognition
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  if (SpeechRecognition) {
    recognitionRef.current = new SpeechRecognition();
    recognitionRef.current.continuous = false;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';
  }

  // Resume data
  const resumeData = {
    "name": "Abhay Keshri",
    "contact": "+91 7001072985 | abhaykeshri57@gmail.com",
    "portfolio": "https://abhay-potfolio.pages.dev/",
    "title": "Web & Mobile App Developer",
    "summary": "Driven BCA graduate with strong foundational knowledge in programming and software development. Passionate about creating user-centric web and mobile applications with a focus on clean design, functionality, and performance. Experienced in full-stack development, delivering responsive and scalable solutions.",
    "experience": [
      {
        "role": "Python Developer Intern",
        "company": "Metavy",
        "period": "June 2023 – August 2023",
        "details": [
          "Built and managed REST APIs and backend systems using Python",
          "Worked with different teams to deploy projects and improve performance",
          "Learned key skills in Python, API development, and backend design"
        ]
      },
      {
        "role": "React Developer (Remote, Full-Time)",
        "company": "Metavy",
        "period": "Aug 2024 – Feb 2025",
        "details": [
          "Developed responsive front-end components using React.js",
          "Collaborated with backend teams to integrate RESTful APIs",
          "Built reusable UI components to maintain code scalability"
        ]
      },
      {
        "role": "Freelance Full-Stack Developer",
        "company": "Self-Employed",
        "period": "March 2025 – Present",
        "details": [
          "Designed and developed custom web applications for clients using React.js, Node.js, and MongoDB",
          "Implemented modern UI/UX designs with Tailwind CSS and Framer Motion",
          "Managed project lifecycles from requirement gathering to deployment"
        ]
      }
    ],
    "projects": [
      {
        "name": "Rehome – Pet Adoption Mobile App",
        "period": "March 2025 – Present",
        "role": "Full-Stack Developer",
        "details": [
          "Cross-platform mobile application for pet adoption using React Native, Expo, and Firebase",
          "Implemented Clerk authentication and Cloudflare R2 for image storage",
          "Integrated RESTful APIs for dynamic content and real-time updates"
        ],
        "links": {
          "apk": "https://expo.dev/accounts/abhaykeshri/projects/rehome/builds/e2ac6aa9-0075-46d0-b223-9ded06003237",
          "website": "https://rehome.pages.dev"
        }
      },
      {
        "name": "Movie Reviewing Web App",
        "period": "March 2024 – Present",
        "role": "Developer",
        "details": [
          "Responsive web app using React.js with TMDB API integration",
          "Implemented search, rating, and review features",
          "Used React hooks for state management"
        ],
        "links": {
          "website": "https://test-9si.pages.dev"
        }
      },
      {
        "name": "KaanchDrive – Secure Cloud Storage",
        "period": "February 2025 – Present",
        "role": "Developer",
        "details": [
          "Secure cloud storage platform using React.js, Tailwind CSS, and Pinata/IPFS",
          "Integrated Kaanch Wallet for blockchain-based authentication",
          "Prioritized encrypted file handling and privacy"
        ],
        "links": {
          "website": "https://kaanchdrive.com"
        }
      },
      {
        "name": "Portfolio Website",
        "period": "January 2025 – Present",
        "role": "Full-Stack Developer",
        "details": [
          "Developed a responsive portfolio website using React.js and create-react-app",
          "Integrated interactive UI components with Tailwind CSS and Framer Motion for animations",
          "Showcased projects, skills, and professional experience with a clean, user-friendly design"
        ],
        "links": {
          "website": "https://abhay-potfolio.pages.dev/"
        }
      }
    ],
    "skills": {
      "programming": ["C", "Python", "HTML", "CSS", "Java", "JavaScript", "TypeScript", "Data Structure and Algorithm"],
      "development": ["React.js", "NodeJs", "ExpressJS", "MongoDb", "Git & GitHub", "Firebase", "Cloudflare", "Tailwind CSS", "Framer Motion"],
      "soft": ["Leadership", "Team Work", "Problem Solving", "Time Management"]
    },
    "education": "Bachelor of Computer Applications from IFIM College, Bangalore",
    "certifications": [
      {
        "name": "Full-Stack Web Development Bootcamp",
        "issuer": "Udemy",
        "year": "2024",
        "details": "Completed an intensive course covering React, Node.js, Express, and MongoDB"
      },
      {
        "name": "React Native Certification",
        "issuer": "Coursera",
        "year": "2025",
        "details": "Mastered mobile app development with React Native and Expo"
      }
    ],
    "social": {
      "instagram": "https://www.instagram.com/_keshri.abhay_/",
      "facebook": "https://www.facebook.com/itsabhay57",
      "linkedin": "https://www.linkedin.com/in/abhay-keshri-857386323/",
      "github": "https://github.com/Abhaykeshri"
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      alert('Please fill out all fields.');
      return;
    }
    const subject = encodeURIComponent(`Message from ${formData.name}`);
    const body = encodeURIComponent(`Name: ${formData.name}\nEmail: ${formData.email}\n\nMessage:\n${formData.message}`);
    window.location.href = `mailto:keshriabhay57@gmail.com?subject=${subject}&body=${body}`;
  };

  const askAIQuestion = () => {
    if (isAudioPlaying || isRecording) return;

    if (!SpeechRecognition) {
      alert('Speech recognition is not supported in this browser. Please type your question.');
      return;
    }

    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    debounceTimeoutRef.current = setTimeout(() => {
      setIsRecording(true);

      recognitionRef.current.onresult = (event) => {
        const transcript = Array.from(event.results)
          .map(result => result[0].transcript)
          .join('');
        
        if (event.results[0].isFinal) {
          processAIQuestion(transcript); // Fixed syntax error here
        }
      };

      recognitionRef.current.onend = () => {
        setIsRecording(false);
      };

      recognitionRef.current.onerror = (error) => {
        console.error('Speech recognition error:', error);
        alert('Failed to recognize speech. Please try again.');
        setIsRecording(false);
      };

      recognitionRef.current.start();
      
    }, 200);
  };

  const processAIQuestion = async (question) => {
    if (!question.trim()) {
      alert('No question detected. Please try again.');
      setIsAudioPlaying(false);
      return;
    }

    setIsAudioPlaying(true);
    setAiResponse('');

    try {
      const apiResponse = await fetchAIResponse(question);
      setAiResponse(apiResponse);
      const utterance = new SpeechSynthesisUtterance(apiResponse);
      utterance.lang = 'en-US';
      utterance.volume = 1;
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.onend = () => {
        setIsAudioPlaying(false);
      };
      utterance.onerror = (error) => {
        console.error('Speech synthesis error:', error);
        setIsAudioPlaying(false);
      };
      window.speechSynthesis.speak(utterance);
    } catch (error) {
      console.error('AI interaction error:', error);
      const fallbackResponse = generateAIResponse(question.toLowerCase());
      setAiResponse(fallbackResponse);
      const utterance = new SpeechSynthesisUtterance(fallbackResponse);
      utterance.lang = 'en-US';
      utterance.volume = 1;
      utterance.rate = 1;
      utterance.pitch = 1;
      utterance.onend = () => {
        setIsAudioPlaying(false);
      };
      utterance.onerror = (error) => {
        console.error('Speech synthesis error:', error);
        setIsAudioPlaying(false);
      };
      window.speechSynthesis.speak(utterance);
    }
  };

  const fetchAIResponse = async (question) => {
    const API_ENDPOINT = 'https://api.x.ai/v1/grok';
    const API_KEY = 'xai-MyjKcLfkeduxkT644phzrPSNgS6Ge8jJhuTNeroomXmlo1kGQgsS32MPdXn9yx71KiT04vOlUEjmRpVD';

    if (!API_KEY) {
      throw new Error('API key not configured. Please set REACT_APP_AI_API_KEY in your .env file.');
    }

    try {
      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${API_KEY}`,
        },
        body: JSON.stringify({
          question: question,
          context: JSON.stringify(resumeData),
        }),
      });

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.response || 'Sorry, I couldn’t generate a response. Please try again.';
    } catch (error) {
      console.error('API call error:', error.message);
      throw error;
    }
  };

  const generateAIResponse = (question) => {
    if (question.includes('who are you') || question.includes('introduce yourself')) {
      return `Hello, reden I'm Abhay Keshri, a Web and Mobile App Developer. ${resumeData.summary} I have experience in both frontend and backend development, with expertise in React, Python, and various web technologies.`;
    }
    
    if (question.includes('contact') || question.includes('how to reach')) {
      return `You can contact Abhay at ${resumeData.contact}. His portfolio website is ${resumeData.portfolio}.`;
    }
    
    if (question.includes('experience') || question.includes('worked')) {
      let response = "Abhay has the following professional experience: ";
      resumeData.experience.forEach(exp => {
        response += `As a ${exp.role} at ${exp.company} from ${exp.period}, he worked on ${exp.details.join(', ')}. `;
      });
      return response;
    }
    
    if (question.includes('metavy') || question.includes('internship')) {
      const internship = resumeData.experience.find(exp => exp.role.includes('Intern'));
      if (internship) {
        return `During his internship at ${internship.company} as a ${internship.role}, Abhay ${internship.details.join(', ')}.`;
      }
      return "Abhay completed an internship at Metavy as a Python Developer Intern.";
    }
    
    if (question.includes('project') || question.includes('built')) {
      let response = "Abhay has worked on several projects including: ";
      resumeData.projects.forEach(proj => {
        response += `${proj.name}, a ${proj.role} project where he ${proj.details.join(', ')}. `;
      });
      return response;
    }
    
    if (question.includes('rehome') || question.includes('pet')) {
      const project = resumeData.projects.find(p => p.name.includes('Rehome'));
      return `Rehome is a pet adoption mobile app developed by Abhay. It's built with React Native and features ${project.details.join(', ')}. You can check it out at ${project.links.website}`;
    }
    
    if (question.includes('movie') || question.includes('tmdb')) {
      const project = resumeData.projects.find(p => p.name.includes('Movie'));
      return `The Movie Reviewing Web App is a React.js application that integrates with TMDB API. It features ${project.details.join(', ')}. Visit it at ${project.links.website}`;
    }
    
    if (question.includes('cloud') || question.includes('kaanch')) {
      const project = resumeData.projects.find(p => p.name.includes('Kaanch'));
      return `KaanchDrive is a secure cloud storage platform developed by Abhay. It uses ${project.details.join(', ')}. Check it out at ${project.links.website}`;
    }
    
    if (question.includes('skill') || question.includes('technology')) {
      return `Abhay's technical skills include programming languages like ${resumeData.skills.programხ

.programming.join(', ')}, and development skills in ${resumeData.skills.development.join(', ')}. He also has strong ${resumeData.skills.soft.join(', ')} skills.`;
    }
    
    if (question.includes('react') || question.includes('frontend')) {
      return `Abhay is proficient in React.js and has used it in several projects including the Movie Reviewing Web App and Rehome pet adoption app. He has experience building responsive components and integrating APIs with React.`;
    }
    
    if (question.includes('python') || question.includes('backend')) {
      return `Abhay has Python development experience from his internship at Metavy, where he built REST APIs and backend systems.`;
    }
    
    if (question.includes('education') || question.includes('degree')) {
      return `Abhay holds a ${resumeData.education}.`;
    }
    
    if (question.includes('linkedin') || question.includes('profile')) {
      return `You can find Abhay's LinkedIn profile at ${resumeData.social.linkedin}`;
    }
    
    if (question.includes('github') || question.includes('code')) {
      return `Abhay's GitHub profile is available at ${resumeData.social.github} where you can see his projects and contributions.`;
    }
    if (question.includes('inspired') || question.includes('motivation')) {
      return `Abhay was inspired to become a developer by ${resumeData.inspiration || 'his passion for solving real-world problems through technology'}.`;
    }

    if (question.includes('favorite project')) {
      const favorite = resumeData.favoriteProject || resumeData.projects[0];
      return `Abhay's favorite project is ${favorite.name} because ${favorite.impact || 'it aligns with his passion and skills'}.`;
    }

    if (question.includes('open source') || question.includes('open-source')) {
      const openSource = resumeData.openSource || [];
      return openSource.length > 0
        ? `Abhay has contributed to open-source projects like ${openSource.map(p => p.name).join(', ')}. Check his GitHub for details: ${resumeData.social.github}`
        : "Abhay has not yet contributed to open-source projects but is open to exploring opportunities.";
    }

    if (question.includes('certification')) {
      return `Abhay holds certifications in ${resumeData.certifications?.map(c => c.name).join(', ') || 'various technologies, including React and AWS'}.`;
    }

    if (question.includes('current') || question.includes('working on')) {
      return `Abhay is currently working on ${resumeData.currentProjects?.map(p => p.name).join(', ') || 'new and exciting projects'}.`;
    }
    
    return "I'm Abhay's virtual assistant. I can tell you about his professional experience, projects, skills, and education. Please ask me something specific about his background or work.";
  };

  return (
    <section id="contact">
      <style>
        {`
          .contact-section {
            min-height: 100vh;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            position: relative;
            padding: 20px;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
            background: #000000;
            overflow: hidden;
            animation: fadeIn 1.5s ease-in-out;
          }

          .contact-container {
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
            max-width: 1200px;
            padding: 40px;
            gap: 40px;
            position: relative;
            z-index: 1;
          }

          .form-container {
            background: rgba(255, 255, 255, 0.08);
            backdrop-filter: blur(12px);
            padding: 40px;
            border-radius: 20px;
            width: 100%;
            max-width: 500px;
            transform: translateY(50px);
            opacity: 0;
            animation: slideIn 0.8s ease-out forwards;
            animation-delay: 0.2s;
            box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
            border: 1px solid rgba(255, 255, 255, 0.1);
          }

          .form-container h2 {
            font-size: 3.5rem;
            font-weight: 800;
            color: #ffffff;
            margin-bottom: 10px;
            background: linear-gradient(90deg, #a0a0ff, #ffffff);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
          }

          .form-container h3 {
            font-size: 1.1rem;
            font-weight: 500;
            color: #a0a0ff;
            text-transform: uppercase;
            letter-spacing: 2px;
            margin-bottom: 25px;
          }

          .form-group {
            margin-bottom: 20px;
          }

          .form-group label {
            display: block;
            font-size: 1.1rem;
            color: #ffffff;
            margin-bottom: 8px;
            font-weight: 500;
          }

          .form-group input,
          .form-group textarea {
            width: 100%;
            padding: 12px 16px;
            background: rgba(255, 255, 255, 0.1);
            border: 2px solid rgba(255, 255, 255, 0.2);
            border-radius: 50px;
            color: #ffffff;
            font-size: 1rem;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
          }

          .form-group input:focus,
          .form-group textarea:focus {
            background: rgba(255, 255, 255, 0.15);
            border-color: #a0a0ff;
            box-shadow: 0 6px 20px rgba(160, 160, 255, 0.3);
            outline: none;
          }

          .form-group input::placeholder,
          .form-group textarea::placeholder {
            color: #b0b0ff;
            opacity: 0.6;
          }

          .form-group textarea {
            border-radius: 25px;
            min-height: 120px;
            resize: vertical;
          }

          .submit-button {
            width: 100%;
            padding: 14px;
            background: linear-gradient(90deg, #a0a0ff, #6b6bff);
            border: none;
            border-radius: 50px;
            color: #ffffff;
            font-size: 1.1rem;
            font-weight: 600;
            cursor: pointer;
            transition: all 0.3s ease;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
            text-align: center;
          }

          .submit-button:hover {
            background: linear-gradient(90deg, #6b6bff, #a0a0ff);
            transform: translateY(-2px);
            box-shadow: 0 6px 20px rgba(160, 160, 255, 0.4);
          }

          .spline-container {
            width: 500px;
            height: 500px;
            opacity: 0;
            animation: slideIn 0.8s ease-out forwards;
            animation-delay: 0.4s;
            position: relative;
            cursor: pointer;
          }

          .spline-container spline-canvas {
            width: 100% !important;
            height: 100% !important;
          }

          .spline-heading {
            font-size: 1.5rem;
            font-weight: 600;
            color: #ffffff;
            background: linear-gradient(90deg, #a0a0ff, #ffffff);
            -webkit-background-clip: text;
            -webkit-text-fill-color: transparent;
            margin-bottom: 15px;
            text-align: center;
          }

          .loader {
            display: flex;
            justify-content: center;
            align-items: center;
            height: 100%;
            color: #a0a0ff;
            font-size: 1rem;
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            background: rgba(0, 0, 0, 0.5);
          }

          .recording-overlay {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            background: rgba(0, 0, 0, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            color: #ff4d4d;
            font-size: 1.2rem;
            font-weight: 600;
            z-index: 2;
            border-radius: 20px;
          }

          .ai-response {
            margin-top: 20px;
            padding: 15px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 10px;
            color: #ffffff;
            font-size: 1rem;
            max-width: 100%;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.2);
          }

          .social-icons {
            position: fixed;
            right: 20px;
            top: 50%;
            transform: translateY(-50%);
            display: flex;
            flex-direction: column;
            gap: 15px;
            z-index: 1;
          }

          .social-icon {
            width: 45px;
            height: 45px;
            background: rgba(255, 255, 255, 0.1);
            border-radius: 50%;
            display: flex;
            align-items: center;
            justify-content: center;
            color: #ffffff;
            font-size: 1.4rem;
            transition: all 0.3s ease;
            transform: translateX(20px);
            opacity: 0;
            animation: slideInRight 0.5s ease-out forwards;
            box-shadow: 0 4px 15px rgba(0, 0, 0, 0.3);
          }

          .social-icon:nth-child(1) { animation-delay: 0.4s; }
          .social-icon:nth-child(2) { animation-delay: 0.5s; }
          .social-icon:nth-child(3) { animation-delay: 0.6s; }
          .social-icon:nth-child(4) { animation-delay: 0.7s; }

          .social-icon:hover {
            background: #a0a0ff;
            color: #000000;
            transform: scale(1.1) translateX(0);
            box-shadow: 0 6px 20px rgba(160, 160, 255, 0.4);
          }

          @keyframes fadeIn {
            from { opacity: 0; }
            to { opacity: 1; }
          }

          @keyframes slideIn {
            from { opacity: 0; transform: translateY(50px); }
            to { opacity: 1; transform: translateY(0); }
          }

          @keyframes slideInRight {
            from { opacity: 0; transform: translateX(20px); }
            to { opacity: 1; transform: translateX(0); }
          }

          @media (max-width: 1024px) {
            .contact-container {
              flex-direction: column;
              padding: 20px;
              gap: 30px;
            }
            .form-container {
              max-width: 100%;
              padding: 30px;
            }
            .form-container h2 {
              font-size: 2.8rem;
            }
            .form-container h3 {
              font-size: 1rem;
            }
            .spline-container {
              width: 400px;
              height: 400px;
            }
            .spline-heading {
              font-size: 1.3rem;
            }
            .social-icons {
              position: static;
              transform: none;
              flex-direction: row;
              justify-content: center;
              margin-top: 20px;
              width: 100%;
            }
            .social-icon {
              width: 40px;
              height: 40px;
              font-size: 1.2rem;
            }
            .ai-response {
              padding: 10px;
              font-size: 0.9rem;
            }
          }

          @media (max-width: 768px) {
            .contact-section {
              padding: 15px;
            }
            .contact-container {
              padding: 15px;
            }
            .form-container {
              padding: 20px;
            }
            .form-container h2 {
              font-size: 1.2rem;
              margin-bottom: 8px;
            }
            .form-container h3 {
              font-size: 0.7rem;
              margin-bottom: 15px;
              letter-spacing: 1.5px;
            }
            .form-group input,
            .form-group textarea {
              padding: 10px 15px;
              font-size: 0.9rem;
            }
            .form-group textarea {
              min-height: 100px;
            }
            .submit-button {
              padding: 12px;
              font-size: 1rem;
            }
            .spline-container {
              width: 300px;
              height: 300px;
            }
            .spline-heading {
              font-size: 1.1rem;
            }
            .recording-overlay {
              font-size: 1rem;
            }
            .ai-response {
              font-size: 0.8rem;
            }
          }

          @media (max-width: 480px) {
            .form-container h2 {
              font-size: 1rem;
              margin-bottom: 6px;
            }
            .form-container h3 {
              font-size: 0.6rem;
              margin-bottom: 12px;
              letter-spacing: 1px;
            }
            .form-group label {
              font-size: 0.9rem;
            }
            .form-group input,
            .form-group textarea {
              padding: 8px 12px;
              font-size: 0.8rem;
            }
            .submit-button {
              padding: 10px;
              font-size: 0.9rem;
            }
            .spline-container {
              width: 250px;
              height: 250px;
            }
            .spline-heading {
              font-size: 0.9rem;
              margin-bottom: 10px;
            }
            .social-icons {
              margin-top: 15px;
              gap: 10px;
            }
            .social-icon {
              width: 35px;
              height: 35px;
              font-size: 1rem;
            }
            .recording-overlay {
              font-size: 0.9rem;
            }
            .ai-response {
              font-size: 0.7rem;
              padding: 8px;
            }
          }
        `}
      </style>
      <div className="contact-section">
        <div className="contact-container">
          <div className="form-container">
            <h3>GET IN TOUCH</h3>
            <h2>Contact.</h2>
            <form onSubmit={handleSubmit}>
              <div className="form-group">
                <label htmlFor="name">Your Name</label>
                <input
                  id="name"
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="What's your good name?"
                  required
                  aria-required="true"
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">Your Email</label>
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="What's your email address?"
                  required
                  aria-required="true"
                />
              </div>
              <div className="form-group">
                <label htmlFor="message">Your Message</label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="What do you want to say?"
                  required
                  aria-required="true"
                ></textarea>
              </div>
              <button type="submit" className="submit-button">Send Message</button>
            </form>
          </div>
          <div className="spline-container">
            <h2 className="spline-heading">✨ Click Me & Ask About Abhay ✨</h2>
            {!loaded && <div className="loader">Loading...</div>}
            {isRecording && (
              <div className="recording-overlay">
                Recording...
              </div>
            )}
            <Spline
              scene="https://prod.spline.design/TjveeJ-gySINU8cy/scene.splinecode"
              onLoad={() => setLoaded(true)}
              onError={(error) => console.error('Spline error:', error)}
              onMouseDown={askAIQuestion}
            />
            {aiResponse && (
              <div className="ai-response">
                <strong>AI Response:</strong> {aiResponse}
              </div>
            )}
          </div>
        </div>
        <div className="social-icons">
          <a
            href={resumeData.social.instagram}
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon"
            aria-label="Instagram"
          >
            <FaInstagram />
          </a>
          <a
            href={resumeData.social.facebook}
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon"
            aria-label="Facebook"
          >
            <FaFacebookF />
          </a>
          <a
            href={resumeData.social.linkedin}
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon"
            aria-label="LinkedIn"
          >
            <FaLinkedinIn />
          </a>
          <a
            href={resumeData.social.github}
            target="_blank"
            rel="noopener noreferrer"
            className="social-icon"
            aria-label="GitHub"
          >
            <FaGithub />
          </a>
        </div>
      </div>
    </section>
  );
};

export default Contact;